import { useEffect } from "react";
import "./ResponsiveModalSheets.css";

const MOBILE_QUERY = "(max-width: 767px)";
const DRAG_THRESHOLD = 64;

const ResponsiveModalSheets = () => {
  useEffect(() => {
    const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;

    const closeModal = (modal) => {
      const nativeClose = modal.querySelector(
        '[data-bs-dismiss="modal"], .modal-header .btn-close, .modal-header button[aria-label="Close"], .modal-header button[aria-label="Cerrar"]'
      );

      if (nativeClose) {
        nativeClose.click();
        return;
      }

      window.bootstrap?.Modal?.getOrCreateInstance(modal)?.hide();
    };

    const enhanceModal = (modal) => {
      const dialog = modal.querySelector(":scope > .modal-dialog");
      const content = dialog?.querySelector(":scope > .modal-content");
      if (!dialog || !content || dialog.dataset.responsiveSheet === "true") return;

      // Algunos modales se montan dentro de un contenedor Reactstrap con
      // estilos inline. Aplicar el tamaño aquí evita que vuelvan al ancho
      // bootstrap (con márgenes) y unifica el resultado con Videos IA.
      modal.style.setProperty("width", "100dvw", "important");
      modal.style.setProperty("max-width", "100dvw", "important");
      dialog.classList.add("modal-fullscreen", "responsive-modal-sheet-dialog");
      dialog.style.setProperty("width", "100dvw", "important");
      dialog.style.setProperty("max-width", "100dvw", "important");
      dialog.style.setProperty("margin", "0", "important");
      content.style.setProperty("width", "100%", "important");
      content.style.setProperty("max-width", "100%", "important");
      dialog.dataset.responsiveSheet = "true";
      const modalHeader = content.querySelector(":scope > .modal-header");
      const handle = document.createElement("button");
      handle.type = "button";
      handle.className = "responsive-modal-sheet-handle";
      handle.setAttribute("aria-label", "Arrastrar para expandir o contraer el modal");
      handle.innerHTML = '<span aria-hidden="true"></span>';
      (modalHeader || content).prepend(handle);

      let startY = null;
      const resetDrag = () => dialog.style.removeProperty("--responsive-sheet-drag-offset");

      const onPointerMove = (event) => {
        if (startY === null) return;
        dialog.style.setProperty("--responsive-sheet-drag-offset", `${event.clientY - startY}px`);
      };

      const onPointerUp = (event) => {
        if (startY === null) return;
        const offset = event.clientY - startY;
        const isExpanded = modal.classList.contains("responsive-modal-sheet-expanded");
        if (offset <= -DRAG_THRESHOLD) modal.classList.add("responsive-modal-sheet-expanded");
        if (offset >= DRAG_THRESHOLD) {
          if (isExpanded) {
            modal.classList.remove("responsive-modal-sheet-expanded");
          } else {
            closeModal(modal);
          }
        }
        startY = null;
        resetDrag();
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointercancel", onPointerUp);
      };

      handle.addEventListener("pointerdown", (event) => {
        if (!isMobile()) return;
        startY = event.clientY;
        handle.setPointerCapture?.(event.pointerId);
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);
        window.addEventListener("pointercancel", onPointerUp);
      });

      const hasCloseButton = content.querySelector(
        '[data-bs-dismiss="modal"], .modal-header .btn-close, .modal-header button[aria-label="Close"], .modal-header button[aria-label="Cerrar"]'
      );
      if (!hasCloseButton) {
        const closeButton = document.createElement("button");
        closeButton.type = "button";
        closeButton.className = "responsive-modal-sheet-close";
        closeButton.setAttribute("aria-label", "Cerrar modal");
        closeButton.innerHTML = "&times;";
        closeButton.addEventListener("click", () => closeModal(modal));
        (modalHeader || content).append(closeButton);
      }
    };

    const enhanceOpenModals = () => {
      if (isMobile()) {
        document.querySelectorAll(".modal").forEach(enhanceModal);
        return;
      }

      document.querySelectorAll(".modal").forEach((modal) => {
        const dialog = modal.querySelector(":scope > .modal-dialog");
        const content = dialog?.querySelector(":scope > .modal-content");
        // No tocar modales que nunca se mejoraron en móvil: podrían tener
        // tamaños inline propios para escritorio.
        if (!dialog || dialog.dataset.responsiveSheet !== "true") return;
        modal.style.removeProperty("width");
        modal.style.removeProperty("max-width");
        dialog.classList.remove("modal-fullscreen", "responsive-modal-sheet-dialog");
        dialog.style.removeProperty("width");
        dialog.style.removeProperty("max-width");
        dialog.style.removeProperty("margin");
        content?.style.removeProperty("width");
        content?.style.removeProperty("max-width");
        delete dialog.dataset.responsiveSheet;
      });
    };

    const closeOnResponsiveBackdrop = (event) => {
      if (!isMobile()) return;

      // Bootstrap modals receive the click on the modal root, while
      // Reactstrap renders a separate .modal-backdrop element.  In both
      // cases only the topmost visible modal is allowed to close.
      if (event.target.matches(".modal")) {
        closeModal(event.target);
        return;
      }

      if (!event.target.matches(".modal-backdrop")) return;
      const visibleModals = [...document.querySelectorAll(".modal.show")];
      const topModal = visibleModals[visibleModals.length - 1];
      if (topModal) closeModal(topModal);
    };

    enhanceOpenModals();
    const observer = new MutationObserver(enhanceOpenModals);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", enhanceOpenModals);
    document.addEventListener("click", closeOnResponsiveBackdrop, true);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", enhanceOpenModals);
      document.removeEventListener("click", closeOnResponsiveBackdrop, true);
    };
  }, []);

  return null;
};

export default ResponsiveModalSheets;
